import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Send } from "lucide-react";
import { api } from "@/lib/apiClient";
import { formatAuditTimestamp } from "@/lib/auditFormat";
import { addDaysLocal, formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import { bookingStatusLabel } from "@/lib/bookingStatus";
import { getUserDisplayName, useCurrentUser } from "@/lib/auth";
import { canWriteInvoice } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { EmailHtmlEditor, type EmailHtmlEditorHandle } from "@/components/mail/EmailHtmlEditor";
import { mailTokenLabel, senderPreviewValues } from "@/lib/templatePlaceholders";
import { FinishedMailPreview } from "@/components/mail/FinishedMailPreview";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import type { ArrivalTypeRecord, PitchCategory } from "@/types/camping";
import {
  ChoiceCard,
  ConstraintFields,
  DisclosureButton,
  PlaceholderGroups,
  PreviewPanel,
  StatusPill,
  StepHeading,
  SwitchRow,
  TEXT_STARTERS,
  constraintCount,
  offsetCopy,
  timingFieldCopy,
} from "@/pages/mail/bulkMailUi";

type Tab = "once" | "rules" | "log";
type StayHistory = "any" | "first_stay" | "returning";

interface Option {
  id: string;
  label: string;
  description?: string;
  fields?: string | null;
  offset?: string | null;
}

interface Placeholder {
  key: string;
  description: string;
}

interface LayoutOption {
  id: string;
  name: string;
  description: string | null;
  source: string;
  html?: string | null;
}

interface Meta {
  timings: Option[];
  triggers: Option[];
  placeholders: Placeholder[];
  statuses: Option[];
  sources: Option[];
  guest_types: Option[];
  layouts: LayoutOption[];
}

interface Constraints {
  statuses: string[];
  pitch_category_ids: string[];
  arrival_types: string[];
  guest_types: string[];
  country_codes: string[];
  sources: string[];
  pitch_codes: string[];
  min_nights: number | null;
  max_nights: number | null;
  min_persons: number | null;
  max_persons: number | null;
  min_adults: number | null;
  max_adults: number | null;
  min_children: number | null;
  max_children: number | null;
  min_dogs: number | null;
  max_dogs: number | null;
  has_children: boolean | null;
  has_dogs: boolean | null;
  wants_electricity: boolean | null;
  has_vehicle: boolean | null;
  pitch_assigned: boolean | null;
  stay_history: StayHistory;
  exclude_place_banned: boolean;
  price_pending: boolean | null;
}

interface Audience extends Constraints {
  timing: string;
  date_from: string | null;
  date_to: string | null;
  on_date: string | null;
  within_days: number | null;
}

interface PreviewRecipient {
  booking_id: string;
  booking_number: string;
  guest_name: string;
  email: string;
  arrival_date: string;
  departure_date: string;
  status: string;
  pitch_code: string | null;
}

interface Preview {
  recipient_count: number;
  skipped_without_email: number;
  skipped_duplicate_email: number;
  truncated: boolean;
  applied_statuses: string[];
  recipients: PreviewRecipient[];
  sample_to: string | null;
  sample_subject: string | null;
  sample_body_text: string | null;
}

interface Rule {
  id: string;
  name: string;
  enabled: boolean;
  trigger_kind: string;
  trigger_label: string;
  offset_days: number;
  filters: Constraints;
  subject_template: string;
  body_text_template: string;
  email_layout_id: string | null;
  append_signature: boolean;
}

interface RuleRunItem {
  rule_id: string;
  name: string;
  matched: number;
  sent: number;
  failed: number;
  skipped_already: number;
  skipped_without_email: number;
  skipped_duplicate_email: number;
  errors: string[];
}

interface RuleRun {
  on_date: string;
  dry_run: boolean;
  rules: RuleRunItem[];
  resumed_broadcasts: number;
}

interface BroadcastRecipient {
  id: string;
  booking_number: string;
  guest_name: string;
  to_address: string;
  status: string;
  error: string | null;
}

interface Broadcast {
  id: string;
  subject_template: string;
  status: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  skipped_without_email: number;
  created_at: string;
  finished_at: string | null;
  recipients: BroadcastRecipient[];
}

interface Delivery {
  id: string;
  rule_name: string;
  booking_number: string;
  guest_name: string;
  to_address: string;
  anchor_date: string;
  status: string;
  error: string | null;
  created_at: string;
}

const BROADCAST_STATUS: Record<string, string> = {
  sending: "Wird gesendet",
  sent: "Gesendet",
  partial: "Teilweise gesendet",
  failed: "Fehlgeschlagen",
};

const DELIVERY_STATUS: Record<string, string> = {
  pending: "Wartet",
  sending: "Wird gesendet",
  sent: "Gesendet",
  failed: "Fehlgeschlagen",
};

function emptyConstraints(): Constraints {
  return {
    statuses: [],
    pitch_category_ids: [],
    arrival_types: [],
    guest_types: [],
    country_codes: [],
    sources: [],
    pitch_codes: [],
    min_nights: null,
    max_nights: null,
    min_persons: null,
    max_persons: null,
    min_adults: null,
    max_adults: null,
    min_children: null,
    max_children: null,
    min_dogs: null,
    max_dogs: null,
    has_children: null,
    has_dogs: null,
    wants_electricity: null,
    has_vehicle: null,
    pitch_assigned: null,
    stay_history: "any",
    exclude_place_banned: true,
    price_pending: null,
  };
}

function insertToken(
  field: HTMLTextAreaElement | HTMLInputElement | null,
  value: string,
  token: string,
  setValue: (next: string) => void,
) {
  const snippet = `{{${token}}}`;
  if (!field) {
    setValue(`${value}${snippet}`);
    return;
  }
  const start = field.selectionStart ?? value.length;
  const end = field.selectionEnd ?? value.length;
  const next = `${value.slice(0, start)}${snippet}${value.slice(end)}`;
  setValue(next);
  window.requestAnimationFrame(() => {
    field.focus();
    const cursor = start + snippet.length;
    field.setSelectionRange(cursor, cursor);
  });
}

export function BulkMailPage({ embedded = false }: { embedded?: boolean }) {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const canSend = canWriteInvoice(user?.groups ?? []);
  const senderSamples = useMemo(
    () =>
      senderPreviewValues({
        name: getUserDisplayName(user),
        email: String(user?.email || user?.upn || ""),
      }),
    [user],
  );
  const [tab, setTab] = useState<Tab>("once");
  const [meta, setMeta] = useState<Meta | null>(null);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const [arrivalTypes, setArrivalTypes] = useState<ArrivalTypeRecord[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [timing, setTiming] = useState("on_site_now");
  const [onDate, setOnDate] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [withinDays, setWithinDays] = useState<number | null>(7);
  const [constraints, setConstraints] = useState<Constraints>(emptyConstraints);
  const [showMore, setShowMore] = useState(false);
  const [subject, setSubject] = useState("Nachricht von {{company_name}}");
  const [body, setBody] = useState("{{salutation}},\n\n");
  const [layoutId, setLayoutId] = useState("");
  const [appendSignature, setAppendSignature] = useState(true);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [broadcast, setBroadcast] = useState<Broadcast | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyEditorRef = useRef<EmailHtmlEditorHandle>(null);
  const [layoutCatalog, setLayoutCatalog] = useState<LayoutOption[]>([]);
  const [insertWhere, setInsertWhere] = useState<"subject" | "body">("body");
  const [previewKey, setPreviewKey] = useState("");
  const [confirmSend, setConfirmSend] = useState(false);
  const [showAllTimings, setShowAllTimings] = useState(false);
  const confirmSendRef = useRef<HTMLDivElement>(null);

  const [rules, setRules] = useState<Rule[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [ruleName, setRuleName] = useState("3 Tage vor Anreise");
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [triggerKind, setTriggerKind] = useState("days_before_arrival");
  const [offsetDays, setOffsetDays] = useState(3);
  const [ruleFilters, setRuleFilters] = useState<Constraints>(emptyConstraints);
  const [ruleSubject, setRuleSubject] = useState("Ihre Anreise am {{arrival_date}}");
  const [ruleBody, setRuleBody] = useState(
    "{{salutation}},\n\nin drei Tagen, am {{arrival_date}}, erwarten wir Sie auf {{company_name}}.\n\nBuchung {{booking_number}}\nAbreise: {{departure_date}}\nPlatz: {{location}}\n\nWir freuen uns auf Sie.",
  );
  const [ruleLayoutId, setRuleLayoutId] = useState("");
  const [ruleSignature, setRuleSignature] = useState(true);
  const [rulePreview, setRulePreview] = useState<Preview | null>(null);
  const [ruleBusy, setRuleBusy] = useState(false);
  const [runResult, setRunResult] = useState<RuleRun | null>(null);
  const ruleSubjectRef = useRef<HTMLInputElement>(null);
  const ruleBodyEditorRef = useRef<EmailHtmlEditorHandle>(null);
  const [ruleInsertWhere, setRuleInsertWhere] = useState<"subject" | "body">("body");
  const [rulePreviewKey, setRulePreviewKey] = useState("");
  const [confirmRun, setConfirmRun] = useState(false);
  const [showAllTriggers, setShowAllTriggers] = useState(false);
  const [showRuleFilters, setShowRuleFilters] = useState(false);
  const [ruleNotice, setRuleNotice] = useState<string | null>(null);

  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [openBroadcastId, setOpenBroadcastId] = useState<string | null>(null);
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    if (embedded) return;
    setPageMeta({
      title: "Rundmail",
      subtitle: "Mails an mehrere Gäste – in drei Schritten",
    });
    return () => setPageMeta(null);
  }, [embedded, setPageMeta]);

  useEffect(() => {
    void api.get<Meta>("/api/v1/mail/bulk/meta").then(setMeta).catch((reason: Error) => setError(reason.message));
    void api.get<LayoutOption[]>("/api/v1/settings/templates/email-layouts").then(setLayoutCatalog).catch(() => setLayoutCatalog([]));
    void api.get<PitchCategory[]>("/api/v1/pitch-categories").then(setCategories).catch(() => setCategories([]));
    void api
      .get<ArrivalTypeRecord[]>("/api/v1/arrival-types")
      .then(setArrivalTypes)
      .catch(() => setArrivalTypes([]));
  }, []);

  const timingOption = meta?.timings.find((item) => item.id === timing);
  const triggerOption = meta?.triggers.find((item) => item.id === triggerKind);
  const statusLabel = useCallback(
    (status: string) => meta?.statuses.find((item) => item.id === status)?.label ?? bookingStatusLabel(status),
    [meta],
  );

  const audience = useMemo<Audience>(() => {
    const fields = timingOption?.fields ?? "none";
    return {
      ...constraints,
      timing,
      on_date: fields === "date" ? onDate || null : null,
      date_from: fields === "range" ? dateFrom || null : null,
      date_to: fields === "range" ? dateTo || null : null,
      within_days: fields === "days" ? withinDays : null,
    };
  }, [constraints, dateFrom, dateTo, onDate, timing, timingOption?.fields, withinDays]);

  const draftKey = useMemo(
    () => JSON.stringify({ audience, subject, body, layoutId, appendSignature }),
    [appendSignature, audience, body, layoutId, subject],
  );
  const previewCurrent = preview !== null && previewKey === draftKey;

  const ruleDraftKey = useMemo(() => {
    const offsetMode = triggerOption?.offset ?? "days_before";
    const minimum = offsetMode === "none" || offsetMode === "days_after_zero" ? 0 : 1;
    const offset = offsetMode === "none" ? 0 : Math.max(offsetDays, minimum);
    return JSON.stringify({
      name: ruleName.trim(),
      enabled: ruleEnabled,
      triggerKind,
      offset,
      ruleFilters,
      ruleSubject,
      ruleBody,
      ruleLayoutId,
      ruleSignature,
    });
  }, [
    offsetDays,
    ruleBody,
    ruleEnabled,
    ruleFilters,
    ruleLayoutId,
    ruleName,
    ruleSignature,
    ruleSubject,
    triggerKind,
    triggerOption?.offset,
  ]);
  const rulePreviewCurrent = rulePreview !== null && rulePreviewKey === ruleDraftKey;

  useEffect(() => {
    setConfirmSend(false);
  }, [draftKey]);

  useEffect(() => {
    setRuleNotice(null);
  }, [ruleDraftKey]);

  useEffect(() => {
    if (confirmSend) confirmSendRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [confirmSend]);

  const loadRules = useCallback(async () => {
    const rows = await api.get<Rule[]>("/api/v1/mail/rules");
    setRules(rows);
  }, []);

  useEffect(() => {
    if (tab === "rules") {
      void loadRules().catch((reason: Error) => setError(reason.message));
    }
  }, [loadRules, tab]);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const [sent, ruleRows] = await Promise.all([
        api.get<Broadcast[]>("/api/v1/mail/broadcasts"),
        api.get<Delivery[]>("/api/v1/mail/rules/deliveries"),
      ]);
      setBroadcasts(sent);
      setDeliveries(ruleRows);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Protokoll konnte nicht geladen werden.");
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "log") void loadLog();
  }, [loadLog, tab]);

  useEffect(() => {
    if (!broadcast || broadcast.status !== "sending") return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void api
        .get<Broadcast>(`/api/v1/mail/broadcasts/${broadcast.id}`)
        .then((row) => {
          if (!cancelled) setBroadcast(row);
        })
        .catch(() => undefined);
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [broadcast]);

  function applyTiming(next: string) {
    setTiming(next);
    setPreview(null);
    const option = meta?.timings.find((item) => item.id === next);
    const today = todayLocal();
    if (option?.fields === "date" && !onDate) setOnDate(today);
    if (option?.fields === "range") {
      if (!dateFrom) setDateFrom(today);
      if (!dateTo) setDateTo(addDaysLocal(today, 6));
    }
    if (option?.fields === "days" && withinDays === null) setWithinDays(7);
  }

  async function runPreview() {
    const key = draftKey;
    setPreviewing(true);
    setError(null);
    setConfirmSend(false);
    try {
      const result = await api.post<Preview>("/api/v1/mail/broadcasts/preview", {
        audience,
        subject_template: subject,
        body_text_template: body,
        email_layout_id: layoutId || null,
        append_signature: appendSignature,
      });
      setPreview(result);
      setPreviewKey(key);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Die Empfänger konnten nicht geladen werden.");
    } finally {
      setPreviewing(false);
    }
  }

  async function sendOnce() {
    if (!previewCurrent || !preview || preview.recipient_count === 0) {
      setError("Bitte zuerst auf „Empfänger anzeigen“ tippen.");
      setConfirmSend(false);
      return;
    }
    setConfirmSend(false);
    setSending(true);
    setError(null);
    try {
      const created = await api.post<Broadcast>("/api/v1/mail/broadcasts", {
        audience,
        subject_template: subject,
        body_text_template: body,
        email_layout_id: layoutId || null,
        append_signature: appendSignature,
      });
      setBroadcast(created);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Die Mail konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  }

  function fillRule(rule: Rule) {
    setEditingId(rule.id);
    setRuleName(rule.name);
    setRuleEnabled(rule.enabled);
    setTriggerKind(rule.trigger_kind);
    setOffsetDays(rule.offset_days);
    setRuleFilters({ ...emptyConstraints(), ...rule.filters });
    setRuleSubject(rule.subject_template);
    setRuleBody(rule.body_text_template);
    setRuleLayoutId(rule.email_layout_id ?? "");
    setRuleSignature(rule.append_signature);
    setRulePreview(null);
    setRulePreviewKey("");
    setRuleNotice(null);
    setShowRuleFilters(constraintCount({ ...emptyConstraints(), ...rule.filters }) > 0);
    const knownTrigger =
      (rule.trigger_kind === "days_before_arrival" && rule.offset_days === 3) ||
      (rule.trigger_kind === "on_arrival" && rule.offset_days === 0) ||
      (rule.trigger_kind === "days_before_departure" && rule.offset_days === 1) ||
      (rule.trigger_kind === "days_after_departure" && rule.offset_days === 1) ||
      (rule.trigger_kind === "stay_day" && rule.offset_days === 2);
    setShowAllTriggers(!knownTrigger);
  }

  function resetRule() {
    setEditingId(null);
    setRuleName("3 Tage vor Anreise");
    setRuleEnabled(true);
    setTriggerKind("days_before_arrival");
    setOffsetDays(3);
    setRuleFilters(emptyConstraints());
    setRuleSubject("Ihre Anreise am {{arrival_date}}");
    setRuleBody(
      "{{salutation}},\n\nin drei Tagen, am {{arrival_date}}, erwarten wir Sie auf {{company_name}}.\n\nBuchung {{booking_number}}\nAbreise: {{departure_date}}\nPlatz: {{location}}\n\nWir freuen uns auf Sie.",
    );
    setRuleLayoutId("");
    setRuleSignature(true);
    setRulePreview(null);
    setRulePreviewKey("");
    setRuleNotice(null);
    setShowRuleFilters(false);
    setShowAllTriggers(false);
  }

  function rulePayload(enabled = ruleEnabled) {
    const offsetMode = triggerOption?.offset ?? "days_before";
    const minimum = offsetMode === "none" || offsetMode === "days_after_zero" ? 0 : 1;
    const offset = offsetMode === "none" ? 0 : Math.max(offsetDays, minimum);
    return {
      name: ruleName.trim(),
      enabled,
      trigger_kind: triggerKind,
      offset_days: offset,
      filters: ruleFilters,
      subject_template: ruleSubject,
      body_text_template: ruleBody,
      email_layout_id: ruleLayoutId || null,
      append_signature: ruleSignature,
    };
  }

  async function saveRule() {
    setRuleBusy(true);
    setError(null);
    try {
      if (editingId) {
        await api.put(`/api/v1/mail/rules/${editingId}`, rulePayload());
      } else {
        const created = await api.post<Rule>("/api/v1/mail/rules", rulePayload());
        setEditingId(created.id);
      }
      await loadRules();
      setRuleNotice(
        ruleEnabled
          ? "Gespeichert. Solange die Regel auf An steht, geht die Mail von allein raus."
          : "Gespeichert. Die Regel ist aus und verschickt nichts.",
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Regel konnte nicht gespeichert werden.");
    } finally {
      setRuleBusy(false);
    }
  }

  async function removeRule(rule: Rule) {
    if (!window.confirm(`Die automatische Mail „${rule.name}“ wirklich löschen? Schon verschickte Mails bleiben unter „Gesendet“.`)) return;
    setError(null);
    try {
      await api.del(`/api/v1/mail/rules/${rule.id}`);
      if (editingId === rule.id) resetRule();
      await loadRules();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Regel konnte nicht gelöscht werden.");
    }
  }

  async function toggleRule(rule: Rule) {
    setError(null);
    try {
      await api.put(`/api/v1/mail/rules/${rule.id}`, {
        name: rule.name,
        enabled: !rule.enabled,
        trigger_kind: rule.trigger_kind,
        offset_days: rule.offset_days,
        filters: rule.filters,
        subject_template: rule.subject_template,
        body_text_template: rule.body_text_template,
        email_layout_id: rule.email_layout_id,
        append_signature: rule.append_signature,
      });
      await loadRules();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Status konnte nicht geändert werden.");
    }
  }

  async function previewRule() {
    const key = ruleDraftKey;
    setRuleBusy(true);
    setError(null);
    try {
      const payload = rulePayload();
      const result = await api.post<Preview>("/api/v1/mail/rules/preview", payload);
      setRulePreview(result);
      setRulePreviewKey(key);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Die Empfänger konnten nicht geladen werden.");
    } finally {
      setRuleBusy(false);
    }
  }

  async function runRules(dryRun: boolean) {
    setConfirmRun(false);
    setRuleBusy(true);
    setError(null);
    try {
      const result = await api.post<RuleRun>("/api/v1/mail/rules/run", { dry_run: dryRun });
      setRunResult(result);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Die Regeln konnten nicht ausgeführt werden.");
    } finally {
      setRuleBusy(false);
    }
  }

  async function openBroadcast(id: string) {
    setOpenBroadcastId(id);
    try {
      const detail = await api.get<Broadcast>(`/api/v1/mail/broadcasts/${id}`);
      setBroadcasts((current) => current.map((item) => (item.id === id ? detail : item)));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Versand konnte nicht geladen werden.");
    }
  }

  function insertIntoComposer(token: string) {
    if (insertWhere === "subject") {
      insertToken(subjectRef.current, subject, token, setSubject);
      return;
    }
    bodyEditorRef.current?.insertPlaceholder(token);
  }

  function insertIntoRule(token: string) {
    if (ruleInsertWhere === "subject") {
      insertToken(ruleSubjectRef.current, ruleSubject, token, setRuleSubject);
      return;
    }
    ruleBodyEditorRef.current?.insertPlaceholder(token);
  }

  function layoutHtml(id: string): string {
    const chosen = layoutCatalog.find((item) => item.id === (id || "classic"));
    return chosen?.html || layoutCatalog.find((item) => item.id === "classic")?.html || "";
  }

  function applyStarter(starter: (typeof TEXT_STARTERS)[number]) {
    const untouched = subject === "Nachricht von {{company_name}}" && body === "{{salutation}},\n\n";
    if (!untouched && !window.confirm("Der bisherige Betreff und Text werden ersetzt. Trotzdem den Vorschlag nehmen?")) return;
    setSubject(starter.subject);
    setBody(starter.body);
    setInsertWhere("body");
  }

  const weekFrom = todayLocal();
  const weekTo = addDaysLocal(weekFrom, 6);
  const quickOn = (id: string) => {
    if (timing !== id) return false;
    if (id === "arriving_within_days") return withinDays === 7;
    if (id === "on_site_between") return dateFrom === weekFrom && dateTo === weekTo;
    return true;
  };
  const anyQuick = [
    "on_site_now",
    "arriving_today",
    "arriving_tomorrow",
    "departing_today",
    "departing_tomorrow",
    "arriving_within_days",
    "on_site_between",
  ].some(quickOn);
  const fieldCopy = timingFieldCopy(timing);
  const activeFilters = constraintCount(constraints);
  const ruleFilterCount = constraintCount(ruleFilters);
  const offsetHelp = offsetCopy(triggerOption?.offset);
  const knownTrigger =
    (triggerKind === "days_before_arrival" && offsetDays === 3) ||
    (triggerKind === "on_arrival" && offsetDays === 0) ||
    (triggerKind === "days_before_departure" && offsetDays === 1) ||
    (triggerKind === "days_after_departure" && offsetDays === 1) ||
    (triggerKind === "stay_day" && offsetDays === 2);
  const sendReady = previewCurrent && preview !== null && preview.recipient_count > 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-3" role="tablist" aria-label="Rundmail">
        {(
          [
            ["once", "Mail schreiben", "Jetzt an mehrere Gäste schicken"],
            ["rules", "Automatisch", "Geht später von allein raus"],
            ["log", "Gesendet", "Was schon rausgegangen ist"],
          ] as const
        ).map(([id, label, hint]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={clsx(
              "rounded-xl border px-4 py-3 text-left transition",
              tab === id ? "border-accent bg-accent-soft ring-1 ring-accent" : "border-separator bg-card hover:bg-fill-tertiary/50",
            )}
            onClick={() => setTab(id)}
          >
            <span className="block text-sm font-semibold text-label">{label}</span>
            <span className="mt-0.5 block text-xs text-label-secondary">{hint}</span>
          </button>
        ))}
      </div>

      {error ? <Alert variant="error">{error}</Alert> : null}

      {tab === "once" ? (
        <div className="space-y-4">
            <Card
              title={<StepHeading step={1} title="An wen geht die Mail?" />}
              description="Eine Gruppe antippen. Die Mail geht an alle Gäste darin, die eine E-Mail-Adresse haben."
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <ChoiceCard selected={quickOn("on_site_now")} title="Am Platz" hint="Alle, die gerade da sind" onClick={() => applyTiming("on_site_now")} />
                  <ChoiceCard selected={quickOn("arriving_today")} title="Anreise heute" hint="Wer heute ankommt" onClick={() => applyTiming("arriving_today")} />
                  <ChoiceCard selected={quickOn("arriving_tomorrow")} title="Anreise morgen" hint="Wer morgen ankommt" onClick={() => applyTiming("arriving_tomorrow")} />
                  <ChoiceCard selected={quickOn("departing_today")} title="Abreise heute" hint="Wer heute fährt" onClick={() => applyTiming("departing_today")} />
                  <ChoiceCard selected={quickOn("departing_tomorrow")} title="Abreise morgen" hint="Wer morgen fährt" onClick={() => applyTiming("departing_tomorrow")} />
                  <ChoiceCard
                    selected={quickOn("arriving_within_days")}
                    title="Nächste 7 Tage"
                    hint="Anreise heute oder bald"
                    onClick={() => {
                      setWithinDays(7);
                      applyTiming("arriving_within_days");
                    }}
                  />
                  <ChoiceCard
                    selected={quickOn("on_site_between")}
                    title="Diese Woche"
                    hint="Von heute bis in 6 Tagen am Platz"
                    onClick={() => {
                      const today = todayLocal();
                      setDateFrom(today);
                      setDateTo(addDaysLocal(today, 6));
                      applyTiming("on_site_between");
                    }}
                  />
                </div>

                <div className="rounded-xl bg-accent-soft px-4 py-3 text-sm">
                  <p className="font-semibold text-label">Ausgewählt: {timingOption?.label ?? "Gruppe"}</p>
                  {timingOption?.description ? <p className="mt-1 text-label-secondary">{timingOption.description}</p> : null}
                </div>

                {showAllTimings || !anyQuick ? (
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-label">Andere Gruppe</span>
                    <span className="mb-1 block text-xs text-label-secondary">Zum Beispiel ein bestimmtes Datum oder Geburtstage.</span>
                    <select className="eh-input w-full" value={timing} onChange={(event) => applyTiming(event.target.value)}>
                      {(meta?.timings ?? []).map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <button type="button" className="text-sm font-medium text-accent" onClick={() => setShowAllTimings(true)}>
                    Andere Gruppe wählen
                  </button>
                )}

                {timingOption?.fields === "date" ? (
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-label">{fieldCopy.date}</span>
                    <input type="date" className="eh-input w-full" value={onDate} onChange={(event) => setOnDate(event.target.value)} />
                  </label>
                ) : null}
                {timingOption?.fields === "range" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm">
                      <span className="mb-1 block font-medium text-label">{fieldCopy.from}</span>
                      <input type="date" className="eh-input w-full" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
                    </label>
                    <label className="block text-sm">
                      <span className="mb-1 block font-medium text-label">{fieldCopy.to}</span>
                      <input type="date" className="eh-input w-full" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
                    </label>
                  </div>
                ) : null}
                {timingOption?.fields === "days" ? (
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-label">{fieldCopy.days}</span>
                    {fieldCopy.daysHint ? <span className="mb-1 block text-xs text-label-secondary">{fieldCopy.daysHint}</span> : null}
                    <input
                      type="number"
                      min={0}
                      max={366}
                      className="eh-input w-full"
                      value={withinDays ?? ""}
                      onChange={(event) => setWithinDays(event.target.value === "" ? null : Number(event.target.value))}
                    />
                  </label>
                ) : null}

                <DisclosureButton
                  open={showMore}
                  title="Nur bestimmte Gäste"
                  hint={
                    activeFilters > 0
                      ? `${activeFilters} ${activeFilters === 1 ? "Einschränkung ist" : "Einschränkungen sind"} aktiv.`
                      : "Optional. Zum Beispiel nur mit Hund oder nur eine Platzkategorie. Ohne Auswahl sind alle dabei."
                  }
                  onClick={() => setShowMore((open) => !open)}
                />
                {showMore ? (
                  <ConstraintFields
                    value={constraints}
                    onChange={setConstraints}
                    meta={meta}
                    categories={categories}
                    arrivalTypes={arrivalTypes}
                  />
                ) : null}
              </div>
            </Card>

          <div className="grid items-start gap-4 xl:grid-cols-2">
            <Card
              title={<StepHeading step={2} title="Was steht in der Mail?" />}
              description="Einen Vorschlag nehmen oder selbst schreiben. Blaue Wörter werden beim Versand zum Namen, Datum oder Platz des Gastes."
            >
              <div className="space-y-4">
                <div className="grid gap-2 sm:grid-cols-3">
                  {TEXT_STARTERS.map((starter) => (
                    <button
                      key={starter.id}
                      type="button"
                      className="rounded-xl border border-separator bg-card px-3 py-2 text-left hover:bg-fill-tertiary/60"
                      onClick={() => applyStarter(starter)}
                    >
                      <span className="block text-sm font-semibold text-label">{starter.label}</span>
                      <span className="mt-0.5 block text-xs text-label-secondary">{starter.hint}</span>
                    </button>
                  ))}
                </div>
                <PlaceholderGroups
                  placeholders={meta?.placeholders ?? []}
                  onInsert={insertIntoComposer}
                  targetLabel={insertWhere === "subject" ? "Betreff" : "Text"}
                />
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-label">Betreff</span>
                  <input
                    ref={subjectRef}
                    className="eh-input w-full"
                    value={subject}
                    onFocus={() => setInsertWhere("subject")}
                    onChange={(event) => setSubject(event.target.value)}
                  />
                </label>
                <div>
                  <span className="mb-1 block text-sm font-medium text-label">Text</span>
                  <EmailHtmlEditor
                    ref={bodyEditorRef}
                    value={body}
                    layoutId={layoutId}
                    conditionFields={(meta?.placeholders ?? []).map((item) => ({
                      key: item.key,
                      label: mailTokenLabel(item.key),
                    }))}
                    onFocus={() => setInsertWhere("body")}
                    onChange={setBody}
                  />
                </div>
                <p className="text-xs text-label-secondary">
                  Wörter in geschweiften Klammern, zum Beispiel {"{{salutation}}"}, bitte stehen lassen. Daraus wird beim Versand die Anrede des Gastes.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-label">HTML-Design</span>
                    <span className="mb-1 block text-xs text-label-secondary">Standard passt fast immer.</span>
                    <select className="eh-input w-full" value={layoutId} onChange={(event) => setLayoutId(event.target.value)}>
                      <option value="">Standard</option>
                      {(meta?.layouts ?? []).map((layout) => (
                        <option key={layout.id} value={layout.id}>{layout.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-start gap-3 rounded-xl border border-separator px-3 py-3 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={appendSignature}
                      onChange={(event) => setAppendSignature(event.target.checked)}
                    />
                    <span>
                      <span className="block font-medium text-label">Grußformel anhängen</span>
                      <span className="mt-0.5 block text-xs text-label-secondary">Am Ende steht automatisch eine Grußformel mit dem Namen des Platzes.</span>
                    </span>
                  </label>
                </div>
              </div>
            </Card>
            <div className="xl:sticky xl:top-[calc(var(--eh-app-header-height)+0.75rem)] xl:z-20 xl:max-h-[calc(100dvh-var(--eh-app-header-height)-1.5rem)] xl:overflow-y-auto">
              <Card
                title="Vorschau"
                description="Genau der Text aus dem Editor. Ist der Text leer, bleibt die Vorschau leer. Steht Text drin, kommt er im gewählten Design. Name des Platzes, Logo und Grußformel kommen dann aus den Betriebsdaten. Angaben zum Gast sind Beispiele."
              >
                <FinishedMailPreview
                  layoutHtml={layoutHtml(layoutId)}
                  layoutId={layoutId}
                  body={body}
                  subject={subject}
                  includeSignature={appendSignature}
                  samples={senderSamples}
                  className="h-[min(32rem,calc(100dvh-var(--eh-app-header-height)-12rem))]"
                />
              </Card>
            </div>
          </div>

          <Card
            title={<StepHeading step={3} title="Kontrollieren und senden" />}
            description="Zuerst die Namen ansehen. Die Mail geht erst raus, wenn Sie das danach bestätigen."
          >
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="lg" variant="secondary" loading={previewing} onClick={() => void runPreview()}>
                  {preview && !previewCurrent ? "Empfänger erneut anzeigen" : "Empfänger anzeigen"}
                </Button>
                <Button
                  type="button"
                  size="lg"
                  disabled={!canSend || !sendReady}
                  leftIcon={<Send size={16} />}
                  onClick={() => setConfirmSend(true)}
                >
                  {sendReady && preview
                    ? `Mail an ${preview.recipient_count} ${preview.recipient_count === 1 ? "Gast" : "Gäste"} senden`
                    : "Mail senden"}
                </Button>
              </div>
              {!canSend ? (
                <p className="text-sm text-label-secondary">Sie können die Mail vorbereiten. Zum Senden braucht es eine Schreibberechtigung.</p>
              ) : null}
              {!previewCurrent && canSend ? (
                <p className="text-sm text-label-secondary">Senden wird möglich, sobald die Empfängerliste zur aktuellen Mail angezeigt wurde.</p>
              ) : null}
              {confirmSend && preview ? (
                <div ref={confirmSendRef} className="rounded-xl border border-status-orange/40 bg-status-orange/10 p-4">
                  <p className="font-semibold text-label">
                    Mail jetzt an {preview.recipient_count} {preview.recipient_count === 1 ? "Gast" : "Gäste"} senden?
                  </p>
                  <p className="mt-1 text-sm text-label-secondary">Die Mail geht sofort raus und lässt sich nicht zurückholen.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="secondary" onClick={() => setConfirmSend(false)}>Abbrechen</Button>
                    <Button type="button" loading={sending} onClick={() => void sendOnce()}>Ja, jetzt senden</Button>
                  </div>
                </div>
              ) : null}
              <PreviewPanel preview={preview} stale={Boolean(preview) && !previewCurrent} statusLabel={statusLabel} />
              {broadcast ? (
                <Alert
                  variant={broadcast.status === "failed" ? "error" : broadcast.status === "sending" ? "info" : broadcast.status === "partial" ? "warning" : "success"}
                  title={BROADCAST_STATUS[broadcast.status] ?? broadcast.status}
                >
                  {broadcast.status === "sending"
                    ? `Bisher ${broadcast.sent_count} von ${broadcast.recipient_count} Mails raus.`
                    : `${broadcast.sent_count} von ${broadcast.recipient_count} Mails sind angekommen.`}
                  {broadcast.failed_count > 0 ? ` ${broadcast.failed_count} konnten nicht zugestellt werden.` : ""}
                </Alert>
              ) : null}
            </div>
          </Card>
        </div>
      ) : null}

      {tab === "rules" ? (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(16rem,22rem)_1fr]">
          <Card
            title="Ihre automatischen Mails"
            description="An heißt: die Mail geht von allein raus. Aus heißt: es passiert nichts."
            actions={
              <Button type="button" size="sm" variant="secondary" onClick={resetRule}>
                Neue Regel
              </Button>
            }
          >
            <ul className="space-y-2">
              {rules.length === 0 ? (
                <li className="text-sm text-label-secondary">
                  Noch keine. Rechts können Sie zum Beispiel eine Erinnerung drei Tage vor der Anreise anlegen.
                </li>
              ) : null}
              {rules.map((rule) => (
                <li key={rule.id} className={clsx("rounded-xl border p-3", editingId === rule.id ? "border-accent bg-accent-soft/50" : "border-separator")}>
                  <button type="button" className="w-full text-left" onClick={() => fillRule(rule)}>
                    <span className="block text-sm font-semibold text-label">{rule.name}</span>
                    <span className="mt-0.5 block text-xs text-label-secondary">{rule.trigger_label}</span>
                  </button>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={rule.enabled}
                      disabled={!canSend}
                      className={clsx(
                        "rounded-full px-3 py-1 text-sm font-semibold disabled:opacity-60",
                        rule.enabled ? "bg-status-green/15 text-status-green" : "bg-fill-tertiary text-label-secondary",
                      )}
                      onClick={() => void toggleRule(rule)}
                    >
                      {rule.enabled ? "An" : "Aus"}
                    </button>
                    <button type="button" className="text-sm text-status-red disabled:opacity-60" disabled={!canSend} onClick={() => void removeRule(rule)}>
                      Löschen
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <div className="space-y-4">
            <div className="grid items-start gap-4 xl:grid-cols-2">
            <Card
              title={editingId ? "Regel ändern" : "Neue automatische Mail"}
              description="Sie stellen einmal ein, wann und an wen. Danach geht die Mail an dem Tag von allein raus."
            >
              <div className="space-y-4">
                <div>
                  <p className="mb-2 text-sm font-medium text-label">Wann soll die Mail rausgehen?</p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    <ChoiceCard
                      selected={triggerKind === "days_before_arrival" && offsetDays === 3}
                      title="3 Tage vor Anreise"
                      hint="Erinnerung vor der Ankunft"
                      onClick={() => {
                        setTriggerKind("days_before_arrival");
                        setOffsetDays(3);
                      }}
                    />
                    <ChoiceCard
                      selected={triggerKind === "on_arrival" && offsetDays === 0}
                      title="Am Anreisetag"
                      hint="Morgens, wenn die Gäste kommen"
                      onClick={() => {
                        setTriggerKind("on_arrival");
                        setOffsetDays(0);
                      }}
                    />
                    <ChoiceCard
                      selected={triggerKind === "days_before_departure" && offsetDays === 1}
                      title="1 Tag vor Abreise"
                      hint="Erinnerung an die Abreise"
                      onClick={() => {
                        setTriggerKind("days_before_departure");
                        setOffsetDays(1);
                      }}
                    />
                    <ChoiceCard
                      selected={triggerKind === "days_after_departure" && offsetDays === 1}
                      title="1 Tag nach Abreise"
                      hint="Zum Beispiel eine kurze Rückfrage"
                      onClick={() => {
                        setTriggerKind("days_after_departure");
                        setOffsetDays(1);
                      }}
                    />
                    <ChoiceCard
                      selected={triggerKind === "stay_day" && offsetDays === 2}
                      title="2. Aufenthaltstag"
                      hint="Der Tag nach der Anreise"
                      onClick={() => {
                        setTriggerKind("stay_day");
                        setOffsetDays(2);
                      }}
                    />
                  </div>
                </div>

                {showAllTriggers || !knownTrigger ? (
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-label">Anderer Zeitpunkt</span>
                    <select
                      className="eh-input w-full"
                      value={triggerKind}
                      onChange={(event) => {
                        const next = event.target.value;
                        setTriggerKind(next);
                        const mode = meta?.triggers.find((item) => item.id === next)?.offset;
                        if (mode === "none") setOffsetDays(0);
                        else if (mode !== "days_after_zero" && offsetDays < 1) setOffsetDays(1);
                      }}
                    >
                      {(meta?.triggers ?? []).map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    {triggerOption?.description ? <span className="mt-1 block text-xs text-label-secondary">{triggerOption.description}</span> : null}
                  </label>
                ) : (
                  <button type="button" className="text-sm font-medium text-accent" onClick={() => setShowAllTriggers(true)}>
                    Anderen Zeitpunkt wählen
                  </button>
                )}

                {triggerOption?.offset !== "none" ? (
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-label">{offsetHelp.label}</span>
                    {offsetHelp.hint ? <span className="mb-1 block text-xs text-label-secondary">{offsetHelp.hint}</span> : null}
                    <input
                      type="number"
                      min={triggerOption?.offset === "days_after_zero" ? 0 : 1}
                      max={triggerOption?.offset === "stay_day" ? 60 : 365}
                      className="eh-input w-full sm:max-w-xs"
                      value={offsetDays}
                      onChange={(event) => setOffsetDays(event.target.value === "" ? 0 : Number(event.target.value))}
                    />
                  </label>
                ) : null}

                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-label">Name der Regel</span>
                  <span className="mb-1 block text-xs text-label-secondary">Damit Sie sie später in der Liste wiederfinden. Die Gäste sehen diesen Namen nicht.</span>
                  <input className="eh-input w-full" value={ruleName} onChange={(event) => setRuleName(event.target.value)} />
                </label>

                <SwitchRow
                  on={ruleEnabled}
                  title="Regel ist eingeschaltet"
                  hint="Ausgeschaltete Regeln verschicken nichts."
                  onToggle={() => setRuleEnabled((value) => !value)}
                />

                <DisclosureButton
                  open={showRuleFilters}
                  title="Nur bestimmte Gäste"
                  hint={
                    ruleFilterCount > 0
                      ? `${ruleFilterCount} ${ruleFilterCount === 1 ? "Einschränkung ist" : "Einschränkungen sind"} aktiv.`
                      : "Optional. Ohne Auswahl gilt die Regel für alle passenden Buchungen."
                  }
                  onClick={() => setShowRuleFilters((open) => !open)}
                />
                {showRuleFilters ? (
                  <ConstraintFields
                    value={ruleFilters}
                    onChange={setRuleFilters}
                    meta={meta}
                    categories={categories}
                    arrivalTypes={arrivalTypes}
                  />
                ) : null}

                <PlaceholderGroups
                  placeholders={meta?.placeholders ?? []}
                  onInsert={insertIntoRule}
                  targetLabel={ruleInsertWhere === "subject" ? "Betreff" : "Text"}
                />
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-label">Betreff</span>
                  <input
                    ref={ruleSubjectRef}
                    className="eh-input w-full"
                    value={ruleSubject}
                    onFocus={() => setRuleInsertWhere("subject")}
                    onChange={(event) => setRuleSubject(event.target.value)}
                  />
                </label>
                <div>
                  <span className="mb-1 block text-sm font-medium text-label">Text</span>
                  <EmailHtmlEditor
                    ref={ruleBodyEditorRef}
                    value={ruleBody}
                    layoutId={ruleLayoutId}
                    minHeightClass="min-h-48"
                    conditionFields={(meta?.placeholders ?? []).map((item) => ({
                      key: item.key,
                      label: mailTokenLabel(item.key),
                    }))}
                    onFocus={() => setRuleInsertWhere("body")}
                    onChange={setRuleBody}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium text-label">HTML-Design</span>
                    <span className="mb-1 block text-xs text-label-secondary">Standard passt fast immer.</span>
                    <select className="eh-input w-full" value={ruleLayoutId} onChange={(event) => setRuleLayoutId(event.target.value)}>
                      <option value="">Standard</option>
                      {(meta?.layouts ?? []).map((layout) => (
                        <option key={layout.id} value={layout.id}>{layout.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-start gap-3 rounded-xl border border-separator px-3 py-3 text-sm">
                    <input type="checkbox" className="mt-0.5" checked={ruleSignature} onChange={(event) => setRuleSignature(event.target.checked)} />
                    <span>
                      <span className="block font-medium text-label">Grußformel anhängen</span>
                      <span className="mt-0.5 block text-xs text-label-secondary">Am Ende steht automatisch eine Grußformel mit dem Namen des Platzes.</span>
                    </span>
                  </label>
                </div>

                {ruleNotice ? <Alert variant="success" title={ruleNotice} /> : null}

                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="lg" variant="secondary" loading={ruleBusy} onClick={() => void previewRule()}>
                    Wer bekäme sie heute?
                  </Button>
                  <Button type="button" size="lg" loading={ruleBusy} disabled={!canSend} onClick={() => void saveRule()}>
                    Regel speichern
                  </Button>
                </div>
                {!canSend ? <p className="text-sm text-label-secondary">Zum Speichern braucht es eine Schreibberechtigung.</p> : null}
              </div>
            </Card>
            <div className="xl:sticky xl:top-[calc(var(--eh-app-header-height)+0.75rem)] xl:z-20 xl:max-h-[calc(100dvh-var(--eh-app-header-height)-1.5rem)] xl:overflow-y-auto">
              <Card
                title="Vorschau"
                description="Genau der Text aus dem Editor. Ist der Text leer, bleibt die Vorschau leer. Steht Text drin, kommt er im gewählten Design. Name des Platzes, Logo und Grußformel kommen dann aus den Betriebsdaten. Angaben zum Gast sind Beispiele."
              >
                <FinishedMailPreview
                  layoutHtml={layoutHtml(ruleLayoutId)}
                  layoutId={ruleLayoutId}
                  body={ruleBody}
                  subject={ruleSubject}
                  includeSignature={ruleSignature}
                  samples={senderSamples}
                  className="h-[min(32rem,calc(100dvh-var(--eh-app-header-height)-12rem))]"
                />
              </Card>
            </div>
            </div>

            <PreviewPanel
              preview={rulePreview}
              stale={Boolean(rulePreview) && !rulePreviewCurrent}
              staleHint="Die Regel hat sich geändert. Noch einmal auf „Wer bekäme sie heute?“ tippen."
              statusLabel={statusLabel}
            />

            <Card
              title="Heute fällige Mails"
              description="Hier sehen Sie, welche eingeschalteten Mails heute rausgehen würden. „Nur ansehen“ verschickt nichts."
            >
              <div className="space-y-3">
                {confirmRun ? (
                  <div className="rounded-xl border border-status-orange/40 bg-status-orange/10 p-4">
                    <p className="font-semibold text-label">Alle eingeschalteten Regeln für heute jetzt ausführen?</p>
                    <p className="mt-1 text-sm text-label-secondary">Es gehen nur die Mails raus, die heute fällig sind.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" onClick={() => setConfirmRun(false)}>Abbrechen</Button>
                      <Button type="button" loading={ruleBusy} onClick={() => void runRules(false)}>Ja, heute senden</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="secondary" loading={ruleBusy} onClick={() => void runRules(true)}>
                      Nur ansehen
                    </Button>
                    <Button type="button" loading={ruleBusy} disabled={!canSend} onClick={() => setConfirmRun(true)}>
                      Heute senden
                    </Button>
                  </div>
                )}
                {runResult ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-label-secondary">
                      Stand {formatIsoDateShort(runResult.on_date)}.
                      {runResult.dry_run ? " Es wurde nichts gesendet." : " Die fälligen Mails wurden abgeschickt."}
                    </p>
                    {runResult.rules.length === 0 ? <p>Heute ist keine automatische Mail fällig.</p> : null}
                    <ul className="space-y-2">
                      {runResult.rules.map((item) => (
                        <li key={item.rule_id} className="rounded-xl border border-separator px-3 py-2">
                          <span className="font-medium text-label">{item.name}</span>
                          <span className="mt-0.5 block text-label-secondary">
                            {item.matched} passen, {item.sent} {runResult.dry_run ? "würden eine Mail bekommen" : "Mails sind raus"}
                            {item.failed > 0 ? `, ${item.failed} nicht angekommen` : ""}
                            {item.skipped_already > 0 ? `, ${item.skipped_already} heute schon angeschrieben` : ""}
                          </span>
                          {item.errors.length > 0 ? <span className="mt-1 block text-status-red">{item.errors.join(" ")}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </Card>
          </div>
        </div>
      ) : null}

      {tab === "log" ? (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <Card
            title="Selbst geschriebene Mails"
            description="Mails, die jemand hier von Hand verschickt hat."
            actions={
              <Button type="button" size="sm" variant="secondary" loading={logLoading} onClick={() => void loadLog()}>
                Aktualisieren
              </Button>
            }
          >
            <ul className="space-y-2">
              {broadcasts.length === 0 ? (
                <li className="text-sm text-label-secondary">Noch keine. Unter „Mail schreiben“ können Sie die erste verschicken.</li>
              ) : null}
              {broadcasts.map((item) => (
                <li key={item.id} className="rounded-xl border border-separator p-3 text-sm">
                  <button type="button" className="w-full text-left" onClick={() => (openBroadcastId === item.id ? setOpenBroadcastId(null) : void openBroadcast(item.id))}>
                    <span className="flex items-start justify-between gap-3">
                      <span className="font-medium text-label">{item.subject_template}</span>
                      <StatusPill status={item.status} label={BROADCAST_STATUS[item.status] ?? item.status} />
                    </span>
                    <span className="mt-1 block text-xs text-label-secondary">
                      {formatAuditTimestamp(item.created_at)} · {item.sent_count} von {item.recipient_count} angekommen
                      {item.failed_count > 0 ? ` · ${item.failed_count} nicht angekommen` : ""}
                    </span>
                  </button>
                  {openBroadcastId === item.id && item.recipients.length > 0 ? (
                    <ul className="mt-3 space-y-1 border-t border-separator pt-3 text-xs text-label-secondary">
                      {item.recipients.map((recipient) => (
                        <li key={recipient.id}>
                          <span className="font-medium text-label">{recipient.guest_name}</span>
                          {" · "}
                          {recipient.to_address}
                          {" · "}
                          <StatusPill status={recipient.status} label={DELIVERY_STATUS[recipient.status] ?? recipient.status} />
                          {recipient.error ? <span className="mt-0.5 block text-status-red">{recipient.error}</span> : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Automatische Mails" description="Mails, die eine Regel von allein verschickt hat.">
            <ul className="space-y-2 text-sm">
              {deliveries.length === 0 ? <li className="text-label-secondary">Noch keine automatische Mail.</li> : null}
              {deliveries.map((item) => (
                <li key={item.id} className="rounded-xl border border-separator px-3 py-3">
                  <span className="flex items-start justify-between gap-3">
                    <span className="font-medium text-label">{item.guest_name || item.rule_name}</span>
                    <StatusPill status={item.status} label={DELIVERY_STATUS[item.status] ?? item.status} />
                  </span>
                  <span className="mt-1 block text-xs text-label-secondary">
                    {item.rule_name} · {formatAuditTimestamp(item.created_at)} · {item.to_address}
                    {item.booking_number ? ` · Buchung ${item.booking_number}` : ""}
                  </span>
                  {item.error ? <span className="mt-1 block text-xs text-status-red">{item.error}</span> : null}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
